import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getAllBlocks,
  getPlcbyBlockId,
  createPLCUnit
} from '../services/api/implementation/impl';
import Modal from '../../shared/components/ui/Modal';

const DIRECTIONS = ['north', 'east', 'west', 'south'];

const DIRECTION_LABELS = {
  north: 'North',
  east: 'East',
  west: 'West',
  south: 'South'
};

const initialBulkColumnFill = () =>
  DIRECTIONS.reduce((acc, d) => {
    acc[d] = false;
    return acc;
  }, {});

/** Per-direction snapshot of entity text before bulk column fill; used to restore on uncheck. */
const initialBulkColumnSnapshots = () =>
  DIRECTIONS.reduce((acc, d) => {
    acc[d] = null;
    return acc;
  }, {});

/**
 * Remap tokens that are source unit names to the paired target unit (row i → row i).
 * Tokens that are source unit names but have no paired row on the target (e.g. 5th source unit
 * when target has only 3 rows) become empty and are dropped from lists.
 * Other text (Elevator, road, …) is unchanged.
 */
const remapSnapshotEntityUnitNames = (snapshot, srcUnits, tgtUnits, pairCount) => {
  const allSourceUnitNames = new Set();
  srcUnits.forEach((u) => {
    if (u?.unit) allSourceUnitNames.add(u.unit);
  });

  const pairedMap = new Map();
  for (let i = 0; i < pairCount; i += 1) {
    const from = srcUnits[i]?.unit;
    const to = tgtUnits[i]?.unit;
    if (from && to) pairedMap.set(from, to);
  }

  const remapStr = (str) => {
    if (!str || typeof str !== 'string') return str || '';

    const hasDelimiter = /[|,]/.test(str);
    if (!hasDelimiter) {
      const t = str.trim();
      if (!t) return str;
      if (pairedMap.has(t)) return pairedMap.get(t);
      if (allSourceUnitNames.has(t)) return '';
      return str;
    }

    const sep = str.includes('|') ? ' | ' : ', ';
    return str
      .split(/\s*(?:,|\|)\s*/)
      .map((segment) => {
        const t = segment.trim();
        if (!t) return null;
        if (pairedMap.has(t)) return pairedMap.get(t);
        if (allSourceUnitNames.has(t)) return null;
        return t;
      })
      .filter((x) => x != null && x !== '')
      .join(sep);
  };

  const entity = {};
  DIRECTIONS.forEach((dir) => {
    entity[dir] = remapStr(snapshot.entity?.[dir] || '');
  });
  return {
    ...snapshot,
    entity
  };
};

const DirectionsToUnits = () => {
  const { id: projectId } = useParams();
  const queryClient = useQueryClient();
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeCell, setActiveCell] = useState(null); // { levelId, unitId, direction }
  const [editedData, setEditedData] = useState({});
  const [isSimilarLevelsModalOpen, setIsSimilarLevelsModalOpen] = useState(false);
  const [similarSourceLevelId, setSimilarSourceLevelId] = useState('');
  const [similarTargetLevelIds, setSimilarTargetLevelIds] = useState([]);
  /** Independent Housing: header checkboxes — on check, copy first row’s value down the column (per level). */
  const [bulkColumnFill, setBulkColumnFill] = useState(initialBulkColumnFill);
  const bulkColumnSnapshotsRef = useRef(initialBulkColumnSnapshots());

  const { data: blocks, isLoading: isLoadingBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const sortedBlocks = useMemo(() => {
    if (!Array.isArray(blocks)) return [];
    return blocks
      .filter((block) => block?.levels?.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [blocks]);

  useEffect(() => {
    if (sortedBlocks.length > 0 && !activeBlockId) {
      setActiveBlockId(sortedBlocks[0].id);
    }
  }, [sortedBlocks, activeBlockId]);

  const { data: unitsByBlock, isLoading: isLoadingUnits } = useQuery({
    queryKey: ['units-directions', activeBlockId],
    queryFn: () => getPlcbyBlockId(activeBlockId),
    enabled: !!activeBlockId
  });

  const activeBlock = sortedBlocks.find((b) => b.id === activeBlockId);

  const isIndependent = useMemo(() => {
    if (!unitsByBlock?.length) return false;
    const first = unitsByBlock.find((u) => u.accommodation?.accomodationType);
    return first?.accommodation?.accomodationType === 'INDEPENDENT';
  }, [unitsByBlock]);

  const rawAccommodationType = useMemo(() => {
    if (!unitsByBlock?.length) return '';
    const first = unitsByBlock.find((u) => u.accommodation?.accomodationType);
    return first?.accommodation?.accomodationType || '';
  }, [unitsByBlock]);

  const accommodationType = useMemo(() => {
    if (rawAccommodationType === 'GROUP') return 'Group Housing';
    if (rawAccommodationType === 'INDEPENDENT') return 'Independent Housing';
    if (rawAccommodationType === 'PARKING') return 'Parking';
    return rawAccommodationType;
  }, [rawAccommodationType]);

  const levelData = useMemo(() => {
    if (!activeBlock?.levels || !unitsByBlock) return [];

    const sortedLevels = [...activeBlock.levels].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true })
    );

    return sortedLevels.map((level) => {
      const levelUnits = unitsByBlock
        .filter((u) => u.level_id === level.id)
        .filter((u) => u.accommodation?.accomodationType?.toLowerCase() !== 'parking')
        .sort((a, b) =>
          a.unitName.localeCompare(b.unitName, undefined, { numeric: true, sensitivity: 'base' })
        );

      const formatDirection = (direction, unit) => {
        const plcNames = unit.unitPlcs
          ?.filter((p) => p.direction === direction)
          ?.map((p) => p.plc?.name)
          .filter(Boolean)
          .join(', ');

        const entityNames = unit.entityMappings
          ?.filter((e) => e.direction === direction)
          ?.map((e) => e.entity?.name)
          .filter(Boolean)
          .join(', ');

        return [entityNames, plcNames].filter(Boolean).join(' | ') || '---';
      };

      const getEntityNames = (direction, unit) => {
        return (
          unit.entityMappings
            ?.filter((e) => e.direction === direction)
            ?.map((e) => e.entity?.name)
            .filter(Boolean) || []
        );
      };

      const getPlcIds = (direction, unit) => {
        return (
          unit.unitPlcs
            ?.filter((p) => p.direction === direction)
            ?.map((p) => p.plc?.id)
            .filter(Boolean) || []
        );
      };

      const getOtherPlcIds = (unit) => {
        return (
          unit.unitPlcs
            ?.filter((p) => p.direction === 'other')
            ?.map((p) => p.plc?.id)
            .filter(Boolean) || []
        );
      };

      return {
        levelId: level.id,
        name: level.name,
        units: levelUnits.map((unit) => ({
          unitId: unit.id,
          unit: unit.unitName,
          levelId: level.id,
          north: formatDirection('north', unit),
          east: formatDirection('east', unit),
          west: formatDirection('west', unit),
          south: formatDirection('south', unit),
          rawEntityNames: {
            north: getEntityNames('north', unit),
            east: getEntityNames('east', unit),
            west: getEntityNames('west', unit),
            south: getEntityNames('south', unit)
          },
          rawPlcIds: {
            north: getPlcIds('north', unit),
            east: getPlcIds('east', unit),
            west: getPlcIds('west', unit),
            south: getPlcIds('south', unit)
          },
          otherPlcIds: getOtherPlcIds(unit)
        }))
      };
    });
  }, [activeBlock, unitsByBlock]);

  const levelDataWithUnits = useMemo(
    () => levelData.filter((l) => Array.isArray(l.units) && l.units.length > 0),
    [levelData]
  );

  const buildEditedDataFromCurrent = useCallback(() => {
    const data = {};
    levelData.forEach((level) => {
      level.units.forEach((unitRow) => {
        const key = `${level.levelId}-${unitRow.unitId}`;
        data[key] = {
          entity: {
            north: unitRow.rawEntityNames.north.join(', '),
            east: unitRow.rawEntityNames.east.join(', '),
            west: unitRow.rawEntityNames.west.join(', '),
            south: unitRow.rawEntityNames.south.join(', ')
          },
          plcIds: {
            north: unitRow.rawPlcIds.north,
            east: unitRow.rawPlcIds.east,
            west: unitRow.rawPlcIds.west,
            south: unitRow.rawPlcIds.south
          },
          otherPlcIds: unitRow.otherPlcIds
        };
      });
    });
    return data;
  }, [levelData]);

  const handleEditClick = () => {
    setEditedData(buildEditedDataFromCurrent());
    setBulkColumnFill(initialBulkColumnFill());
    bulkColumnSnapshotsRef.current = initialBulkColumnSnapshots();
    setIsEditing(true);
    // Only highlight once the user focuses/starts typing in a cell.
    setActiveCell(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedData({});
    setBulkColumnFill(initialBulkColumnFill());
    bulkColumnSnapshotsRef.current = initialBulkColumnSnapshots();
    setActiveCell(null);
  };

  const handleBulkColumnToggle = useCallback(
    (direction, checked) => {
      if (!checked) {
        const snap = bulkColumnSnapshotsRef.current[direction];
        if (snap) {
          setEditedData((prev) => {
            const next = { ...prev };
            Object.entries(snap).forEach(([key, val]) => {
              const row = next[key];
              if (!row) return;
              next[key] = {
                ...row,
                entity: {
                  ...row.entity,
                  [direction]: val
                }
              };
            });
            return next;
          });
        }
        bulkColumnSnapshotsRef.current = {
          ...bulkColumnSnapshotsRef.current,
          [direction]: null
        };
        setBulkColumnFill((prev) => ({ ...prev, [direction]: false }));
        return;
      }

      const levelsWithMultiple = levelData.filter((l) => l.units?.length >= 2);
      for (const level of levelsWithMultiple) {
        const firstKey = `${level.levelId}-${level.units[0].unitId}`;
        const firstVal = String(editedData[firstKey]?.entity?.[direction] ?? '').trim();
        if (!firstVal) {
          toast.error(
            `${DIRECTION_LABELS[direction]}: the first unit is empty. Fill it before copying down the column.`
          );
          return;
        }
      }

      const snapshot = {};
      levelData.forEach((level) => {
        level.units.forEach((u) => {
          const key = `${level.levelId}-${u.unitId}`;
          snapshot[key] = editedData[key]?.entity?.[direction] ?? '';
        });
      });
      bulkColumnSnapshotsRef.current = {
        ...bulkColumnSnapshotsRef.current,
        [direction]: snapshot
      };

      setBulkColumnFill((prev) => ({ ...prev, [direction]: true }));
      setEditedData((prev) => {
        const next = { ...prev };
        levelData.forEach((level) => {
          const us = level.units;
          if (!us || us.length < 2) return;
          const firstKey = `${level.levelId}-${us[0].unitId}`;
          const firstVal = prev[firstKey]?.entity?.[direction] ?? '';
          for (let i = 1; i < us.length; i += 1) {
            const key = `${level.levelId}-${us[i].unitId}`;
            const row = next[key];
            if (!row) continue;
            next[key] = {
              ...row,
              entity: {
                ...row.entity,
                [direction]: firstVal
              }
            };
          }
        });
        return next;
      });
    },
    [levelData, editedData]
  );

  const handleEntityChange = (levelId, unitId, direction, value) => {
    const key = `${levelId}-${unitId}`;
    setEditedData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        entity: {
          ...prev[key]?.entity,
          [direction]: value
        }
      }
    }));
  };

  const buildSavePayload = useCallback(() => {
    const levelMap = {};

    Object.entries(editedData).forEach(([key, data]) => {
      const [levelId, unitId] = key.split('-').map(Number);
      if (!levelMap[levelId]) {
        levelMap[levelId] = { level_id: levelId, units: [] };
      }

      const plcsByDirection = { north: [], south: [], east: [], west: [] };
      const entityByDirection = { north: [], south: [], east: [], west: [] };

      DIRECTIONS.forEach((dir) => {
        plcsByDirection[dir] = data.plcIds?.[dir] || [];

        const rawText = (data.entity?.[dir] || '').trim();
        entityByDirection[dir] = rawText
          ? rawText.split(',').map((s) => s.trim()).filter(Boolean)
          : [];
      });

      levelMap[levelId].units.push({
        unit_id: unitId,
        plcs: plcsByDirection,
        entity: entityByDirection,
        other: data.otherPlcIds || []
      });
    });

    return Object.values(levelMap);
  }, [editedData]);

  const { mutate: saveDirections, isPending: isSaving } = useMutation({
    mutationFn: (payload) => createPLCUnit(payload),
    onSuccess: () => {
      toast.success('Directions updated successfully');
      setIsEditing(false);
      setEditedData({});
      setBulkColumnFill(initialBulkColumnFill());
      bulkColumnSnapshotsRef.current = initialBulkColumnSnapshots();
      setActiveCell(null);
      queryClient.invalidateQueries({ queryKey: ['units-directions', activeBlockId] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update directions');
    }
  });

  const { mutate: applySimilarDirections, isPending: isApplyingSimilar } = useMutation({
    mutationFn: (payload) => createPLCUnit(payload),
    onSuccess: (data, variables) => {
      console.log('[SimilarToLevels] API success — response:', data);
      console.log('[SimilarToLevels] API success — request payload (variables):', variables);
      toast.success('Directions copied and saved.');
      setIsSimilarLevelsModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['units-directions', activeBlockId] });
    },
    onError: (err) => {
      console.error('[SimilarToLevels] API error — full error:', err);
      console.error('[SimilarToLevels] API error — response:', err?.response);
      console.error('[SimilarToLevels] API error — response.data:', err?.response?.data);
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to copy directions between levels'
      );
    }
  });

  const handleSave = () => {
    const payload = buildSavePayload();
    console.log('DirectionsToUnits - edit payload:', payload);
    if (!payload.length) {
      toast.error('No changes to save');
      return;
    }
    saveDirections(payload);
  };

  const openSimilarLevelsModal = () => {
    if (isEditing) {
      toast.error('Please save or cancel your edits before using Similar to all levels.');
      return;
    }
    if (levelDataWithUnits.length < 2) {
      toast.error('At least two levels with units are required to copy directions.');
      return;
    }
    setSimilarSourceLevelId(String(levelDataWithUnits[0].levelId));
    setSimilarTargetLevelIds(
      levelDataWithUnits.length > 1 ? [String(levelDataWithUnits[1].levelId)] : []
    );
    setIsSimilarLevelsModalOpen(true);
  };

  const snapshotFromUnitRow = (unitRow) => ({
    entity: {
      north: unitRow.rawEntityNames.north.join(', '),
      east: unitRow.rawEntityNames.east.join(', '),
      west: unitRow.rawEntityNames.west.join(', '),
      south: unitRow.rawEntityNames.south.join(', ')
    },
    plcIds: {
      north: [...(unitRow.rawPlcIds.north || [])],
      east: [...(unitRow.rawPlcIds.east || [])],
      west: [...(unitRow.rawPlcIds.west || [])],
      south: [...(unitRow.rawPlcIds.south || [])]
    },
    otherPlcIds: [...(unitRow.otherPlcIds || [])]
  });

  const buildFullBlockPayload = useCallback(
    (overrides = {}) => {
      const levelMap = {};
      levelData.forEach((level) => {
        level.units.forEach((unitRow) => {
          const key = `${level.levelId}-${unitRow.unitId}`;
          const snapshot = overrides[key] || snapshotFromUnitRow(unitRow);
          if (!levelMap[level.levelId]) {
            levelMap[level.levelId] = { level_id: level.levelId, units: [] };
          }
          const plcsByDirection = { north: [], south: [], east: [], west: [] };
          const entityByDirection = { north: [], south: [], east: [], west: [] };
          DIRECTIONS.forEach((dir) => {
            plcsByDirection[dir] = snapshot.plcIds?.[dir] || [];
            const rawText = (snapshot.entity?.[dir] || '').trim();
            entityByDirection[dir] = rawText
              ? rawText.split(',').map((s) => s.trim()).filter(Boolean)
              : [];
          });
          levelMap[level.levelId].units.push({
            unit_id: unitRow.unitId,
            plcs: plcsByDirection,
            entity: entityByDirection,
            other: snapshot.otherPlcIds || []
          });
        });
      });
      return Object.values(levelMap);
    },
    [levelData]
  );

  const handleApplySimilarLevels = () => {
    const fromId = Number(similarSourceLevelId);
    const targetIds = [
      ...new Set(
        similarTargetLevelIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id !== fromId)
      )
    ];

    console.log('[SimilarToLevels] handleApplySimilarLevels — selected ids:', {
      similarSourceLevelId,
      similarTargetLevelIds,
      fromId,
      targetIds
    });

    if (!similarSourceLevelId) {
      toast.error('Please select a source level.');
      return;
    }
    if (targetIds.length === 0) {
      toast.error('Please select at least one target level.');
      return;
    }

    const sourceLevel = levelData.find((l) => l.levelId === fromId);
    if (!sourceLevel) {
      toast.error('Invalid source level.');
      return;
    }

    console.log('[SimilarToLevels] source level:', sourceLevel);

    const srcUnits = sourceLevel.units;
    if (srcUnits.length === 0) {
      toast.error('Source level has no units.');
      return;
    }

    const overrides = {};
    let totalPaired = 0;

    for (const toId of targetIds) {
      const targetLevel = levelData.find((l) => l.levelId === toId);
      if (!targetLevel) continue;

      const tgtUnits = targetLevel.units;
      const n = Math.min(srcUnits.length, tgtUnits.length);
      if (n === 0) continue;

      for (let i = 0; i < n; i += 1) {
        const snap = snapshotFromUnitRow(srcUnits[i]);
        const remapped = remapSnapshotEntityUnitNames(snap, srcUnits, tgtUnits, n);
        // Copy N/E/W/S directions only; keep target unit's "other" PLC list as-is (do not copy source `other`).
        overrides[`${targetLevel.levelId}-${tgtUnits[i].unitId}`] = {
          ...remapped,
          otherPlcIds: [...(tgtUnits[i].otherPlcIds || [])]
        };
      }
      totalPaired += n;
    }

    if (Object.keys(overrides).length === 0) {
      toast.error('No units could be copied to the selected target levels.');
      return;
    }

    console.log('[SimilarToLevels] target levels count:', targetIds.length);
    console.log('[SimilarToLevels] pairing by table row order per target, total unit updates:', totalPaired);
    console.log('[SimilarToLevels] overrides (target keys → snapshot from source):', overrides);

    const payload = buildFullBlockPayload(overrides);
    console.log('[SimilarToLevels] full POST /unitplc payload:', payload);
    applySimilarDirections(payload);
  };

  if (isLoadingBlocks) {
    return (
      <div className="w-full min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!sortedBlocks.length) {
    return (
      <div className="w-full min-h-full p-4 sm:p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No blocks with levels found for this project.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-full p-4 sm:p-6 relative">
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Block Tabs + Edit Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 pt-4 sm:pt-5 pb-3">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            {sortedBlocks.map((block) => (
              <button
                key={block.id}
                type="button"
                onClick={() => {
                  if (isEditing) {
                    toast.error('Please save or cancel your changes first');
                    return;
                  }
                  setActiveBlockId(block.id);
                }}
                className={`px-3 sm:px-5 py-1.5 sm:py-2 text-[11px] sm:text-sm leading-none font-medium rounded-full border transition-all duration-200 whitespace-nowrap ${
                  activeBlockId === block.id
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}>
                {block.name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap justify-end items-center gap-2">
            {rawAccommodationType === 'GROUP' && (
              <button
                type="button"
                onClick={openSimilarLevelsModal}
                disabled={
                  isSaving ||
                  isApplyingSimilar ||
                  isLoadingUnits ||
                  levelDataWithUnits.length < 2 ||
                  isEditing
                }
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-[11px] sm:text-sm leading-none font-medium rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                Similar to all levels
              </button>
            )}
            {!isEditing ? (
              <button
                type="button"
                onClick={handleEditClick}
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-[11px] sm:text-sm leading-none font-medium rounded-md border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-200 whitespace-nowrap">
                Edit
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-[11px] sm:text-sm leading-none font-medium rounded-md border border-red-400 text-red-500 hover:bg-red-50 transition-all duration-200 whitespace-nowrap">
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Accommodation Type */}
        <div className="px-4 sm:px-6 pb-4">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-800">
            {accommodationType ? `For ${accommodationType}` : ''}
          </h3>
        </div>

        {/* Loading state for units */}
        {isLoadingUnits && (
          <div className="border-t border-gray-200 py-16 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Fixed Header + Scrollable Body */}
        {!isLoadingUnits && levelData.length > 0 && (
          <div className="border-t border-gray-200 overflow-x-auto">
            <table className="min-w-[640px] w-full text-[11px] sm:text-sm table-fixed">
              <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20 text-[11px] sm:text-sm">
                <tr className="font-normal">
                  {!isIndependent && <th className="w-16" />}
                  <th className="w-[110px] sm:w-[12%] text-left px-3 sm:px-4 py-3 font-semibold break-words">
                    Units
                  </th>
                  {DIRECTIONS.map((dir) => (
                    <th
                      key={dir}
                      className="text-left px-3 sm:px-4 py-3 font-semibold break-words">
                      {isIndependent && isEditing ? (
                        <label className="flex items-center gap-2 cursor-pointer select-none font-semibold">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 accent-primary shrink-0"
                            checked={!!bulkColumnFill[dir]}
                            onChange={(e) => handleBulkColumnToggle(dir, e.target.checked)}
                            title={`Copy first unit’s ${DIRECTION_LABELS[dir]} value to all units in this column (per level)`}
                          />
                          <span>{DIRECTION_LABELS[dir]}</span>
                        </label>
                      ) : (
                        DIRECTION_LABELS[dir]
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>

            <div className={`overflow-y-auto ${isEditing ? 'max-h-[55vh]' : 'max-h-[60vh]'}`}>
              {levelData.map((level, levelIdx) => (
                <div key={levelIdx} className={levelIdx > 0 ? 'border-t border-gray-200' : ''}>
                  <div className="flex">
                    {!isIndependent && (
                      <div className="flex items-center justify-center w-16 bg-gray-50 border-r border-gray-200 shrink-0">
                        <span
                          className="text-[11px] sm:text-sm font-semibold text-gray-600 whitespace-nowrap"
                          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                          {level.name}
                        </span>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <table className="min-w-[640px] w-full text-[11px] sm:text-sm table-fixed">
                        <tbody>
                          {level.units.length > 0 ? (
                            level.units.map((row, rowIdx) => {
                              const editKey = `${level.levelId}-${row.unitId}`;
                              const unitEdits = editedData[editKey];

                              return (
                                <tr
                                  key={rowIdx}
                                  className={`border-t border-gray-100 ${
                                    rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                                  }`}>
                                  <td className="w-[110px] sm:w-[12%] px-3 sm:px-4 py-2 text-gray-800 font-medium align-middle break-words">
                                    {row.unit}
                                  </td>
                                  {DIRECTIONS.map((dir) => (
                                    (() => {
                                      const isCellEditable = isEditing && !!unitEdits;
                                      const isCellActive =
                                        activeCell?.levelId === level.levelId &&
                                        activeCell?.unitId === row.unitId &&
                                        activeCell?.direction === dir;

                                      return (
                                        <td
                                          key={`${dir}-${isEditing}`}
                                          contentEditable={isCellEditable}
                                          suppressContentEditableWarning
                                          onFocus={() => {
                                            if (isCellEditable) {
                                              setActiveCell({
                                                levelId: level.levelId,
                                                unitId: row.unitId,
                                                direction: dir
                                              });
                                            }
                                          }}
                                          onClick={() => {
                                            if (isCellEditable) {
                                              setActiveCell({
                                                levelId: level.levelId,
                                                unitId: row.unitId,
                                                direction: dir
                                              });
                                            }
                                          }}
                                          onBlur={(e) => {
                                            if (isEditing && unitEdits) {
                                              handleEntityChange(
                                                level.levelId,
                                                row.unitId,
                                                dir,
                                                e.currentTarget.textContent || ''
                                              );
                                            }
                                          }}
                                          className={`px-3 sm:px-4 py-2 text-gray-600 align-middle break-words ${
                                            isCellEditable
                                              ? 'outline-none cursor-text hover:bg-blue-50/40 focus:bg-blue-50/60'
                                              : ''
                                          } ${isCellActive ? 'border-2 border-primary bg-blue-50/60 relative z-10' : ''}`}>
                                          {isEditing && unitEdits
                                            ? unitEdits.entity?.[dir] || ''
                                            : row[dir]}
                                        </td>
                                      );
                                    })()
                                  ))}
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 text-center text-gray-400">
                                No units in this level
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state when no levels/units */}
        {!isLoadingUnits && levelData.length === 0 && (
          <div className="border-t border-gray-200 p-8 text-center text-gray-400">
            No levels found for this block.
          </div>
        )}
      </div>

      {/* Floating Save Button */}
      {rawAccommodationType === 'GROUP' && isSimilarLevelsModalOpen && (
        <Modal
          name="Similar to all levels"
          width={42}
          onClose={() => setIsSimilarLevelsModalOpen(false)}>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-800" htmlFor="similar-source-level">
                Copy from (source level)
              </label>
              <select
                id="similar-source-level"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={similarSourceLevelId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSimilarSourceLevelId(v);
                  setSimilarTargetLevelIds((prev) => prev.filter((id) => id !== v));
                }}>
                <option value="">Select level</option>
                {levelDataWithUnits.map((lvl) => (
                  <option key={lvl.levelId} value={String(lvl.levelId)}>
                    {lvl.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-800">Copy to (target levels)</span>
              <div className="max-h-44 overflow-y-auto border border-gray-300 rounded-md px-3 py-2 flex flex-col gap-2">
                {!similarSourceLevelId ? (
                  <span className="text-sm text-gray-500">
                    Choose a source level above, then select one or more targets.
                  </span>
                ) : levelDataWithUnits.filter((lvl) => String(lvl.levelId) !== similarSourceLevelId)
                    .length === 0 ? (
                  <span className="text-sm text-gray-500">
                    No other levels with units in this block.
                  </span>
                ) : (
                  levelDataWithUnits
                    .filter((lvl) => String(lvl.levelId) !== similarSourceLevelId)
                    .map((lvl) => {
                      const idStr = String(lvl.levelId);
                      return (
                        <label
                          key={lvl.levelId}
                          className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 accent-primary"
                            checked={similarTargetLevelIds.includes(idStr)}
                            onChange={() => {
                              setSimilarTargetLevelIds((prev) =>
                                prev.includes(idStr)
                                  ? prev.filter((x) => x !== idStr)
                                  : [...prev, idStr]
                              );
                            }}
                          />
                          <span>{lvl.name}</span>
                        </label>
                      );
                    })
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsSimilarLevelsModalOpen(false)}
                disabled={isApplyingSimilar}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplySimilarLevels}
                disabled={isApplyingSimilar}
                className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {isApplyingSimilar && (
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {isApplyingSimilar ? 'Saving...' : 'Apply'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {isEditing && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:bottom-6 sm:left-auto sm:right-6 z-50 flex items-center gap-1 sm:gap-3 max-w-[95vw]">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="px-3 sm:px-6 py-2 sm:py-3 text-[11px] sm:text-sm leading-none font-medium rounded-lg border border-gray-300 bg-white text-gray-700 shadow-lg hover:bg-gray-50 transition-all duration-200 whitespace-nowrap">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 sm:px-8 py-2 sm:py-3 text-[11px] sm:text-sm leading-none font-medium rounded-lg bg-primary text-white shadow-lg hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2 whitespace-nowrap">
            {isSaving && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            )}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default DirectionsToUnits;
